/**
 * useDocuments Hook
 * Manages document data operations with localStorage persistence
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  MOCK_DOCUMENTS, 
  MOCK_COMMENTS,
  MOCK_VERSION_HISTORY,
  MOCK_SECTION_CONTENT,
  DOCUMENT_STATUS,
  SECTION_STATUS,
  calculateDocumentProgress,
  calculateAverageConfidence,
} from '@/constants/beStudyMockData';

const STORAGE_KEYS = {
  DOCUMENTS: 'be_study_documents',
  COMMENTS: 'be_study_comments',
  VERSIONS: 'be_study_versions',
};

const DATA_VERSION = 'be_study_data_version';
const CURRENT_DATA_VERSION = 10; // Bump when mock data structure changes

/**
 * Get initial data from localStorage or use mock data
 */
const getInitialData = (key, mockData) => {
  try {
    const storedVersion = localStorage.getItem(DATA_VERSION);
    if (parseInt(storedVersion) !== CURRENT_DATA_VERSION) {
      // Data version mismatch — clear stale cache
      localStorage.removeItem(STORAGE_KEYS.DOCUMENTS);
      localStorage.removeItem(STORAGE_KEYS.COMMENTS);
      localStorage.removeItem(STORAGE_KEYS.VERSIONS);
      localStorage.setItem(DATA_VERSION, CURRENT_DATA_VERSION);
      return mockData;
    }
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error(`Error loading ${key} from localStorage:`, error);
  }
  return mockData;
};

/**
 * Save data to localStorage
 */
const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
};

export function useDocuments() {
  const [documents, setDocuments] = useState(() => getInitialData(STORAGE_KEYS.DOCUMENTS, MOCK_DOCUMENTS));
  const [comments, setComments] = useState(() => getInitialData(STORAGE_KEYS.COMMENTS, MOCK_COMMENTS));
  const [versionHistory, setVersionHistory] = useState(() => getInitialData(STORAGE_KEYS.VERSIONS, MOCK_VERSION_HISTORY));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aiProcessingProgress, setAiProcessingProgress] = useState(0);

  // Persist to localStorage whenever data changes
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.DOCUMENTS, documents);
  }, [documents]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.COMMENTS, comments);
  }, [comments]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.VERSIONS, versionHistory);
  }, [versionHistory]);

  // Generate unique ID
  const generateDocId = useCallback(() => {
    const maxId = documents.reduce((max, d) => {
      const num = parseInt(d.id.replace('DOC-', ''), 10);
      return num > max ? num : max;
    }, 0);
    return `DOC-${String(maxId + 1).padStart(3, '0')}`;
  }, [documents]);

  // Get all documents
  const getAllDocuments = useCallback(() => {
    return documents;
  }, [documents]);

  // Get document by ID
  const getDocument = useCallback((id) => {
    return documents.find(d => d.id === id);
  }, [documents]);

  // Get documents by status
  const getDocumentsByStatus = useCallback((status) => {
    return documents.filter(d => d.status === status);
  }, [documents]);

  // Get documents by user
  const getDocumentsByUser = useCallback((userId) => {
    return documents.filter(d => d.createdBy === userId);
  }, [documents]);

  // Get documents for reviewer
  const getDocumentsForReviewer = useCallback((reviewerId) => {
    return documents.filter(d => d.assignedReviewer === reviewerId);
  }, [documents]);

  // Create new document
  const createDocument = useCallback(async (documentData) => {
    setIsLoading(true);
    setAiProcessingProgress(0);

    try {
      // Simulate AI processing with progress
      const progressInterval = setInterval(() => {
        setAiProcessingProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);

      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 3000));

      clearInterval(progressInterval);
      setAiProcessingProgress(100);

      const newDocId = generateDocId();
      
      // Create sections based on template with mock extracted content
      const newSections = documentData.templateSections.map((section, index) => {
        const mockContent = MOCK_SECTION_CONTENT[section.title] || '';
        const hasContent = mockContent.length > 0;
        return {
          id: `DSEC-${newDocId}-${index + 1}`,
          sectionId: section.id,
          title: section.title,
          status: hasContent ? SECTION_STATUS.IN_PROGRESS : SECTION_STATUS.NOT_STARTED,
          confidenceScore: hasContent ? Math.floor(Math.random() * 15) + 78 : 0,
          content: mockContent,
          uploadedFile: documentData.sectionFiles?.[section.id] || null,
          lastModified: hasContent ? new Date().toISOString() : null,
        };
      });

      const completedCount = newSections.filter(s => s.content).length;
      const totalCount = newSections.length;
      const progress = Math.round((completedCount / totalCount) * 100);

      const newDocument = {
        id: newDocId,
        title: documentData.title,
        templateId: documentData.templateId,
        templateName: documentData.templateName,
        status: DOCUMENT_STATUS.IN_PROGRESS,
        progress,
        confidenceScore: completedCount > 0
          ? Math.round(newSections.filter(s => s.confidenceScore > 0).reduce((sum, s) => sum + s.confidenceScore, 0) / completedCount)
          : 0,
        sections: newSections,
        createdBy: documentData.createdBy || 'USR-003',
        createdByName: documentData.createdByName || 'Current User',
        assignedReviewer: null,
        assignedReviewerName: null,
        dueDate: documentData.dueDate || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Initialize version history with creation + AI extraction
      const now = new Date();
      const initialVersion = {
        id: `VER-${newDocId}-1`,
        version: 1,
        documentId: newDocId,
        createdBy: 'SYSTEM',
        createdByName: 'AI System',
        action: 'CREATED',
        description: 'Document created from template',
        createdAt: new Date(now.getTime() - 5000).toISOString(),
        changes: ['Document initialized', 'Sections created from template'],
      };
      const extractionVersion = {
        id: `VER-${newDocId}-2`,
        version: 2,
        documentId: newDocId,
        createdBy: 'SYSTEM',
        createdByName: 'AI System',
        action: 'EDITED',
        description: `AI extracted content for ${completedCount} of ${totalCount} sections from uploaded source files`,
        createdAt: now.toISOString(),
        changes: newSections.filter(s => s.content).map(s => `${s.title} — draft generated`),
      };

      setDocuments(prev => [...prev, newDocument]);
      setVersionHistory(prev => ({
        ...prev,
        [newDocId]: [initialVersion, extractionVersion],
      }));
      setComments(prev => ({
        ...prev,
        [newDocId]: [],
      }));

      setIsLoading(false);
      setAiProcessingProgress(0);
      
      return { success: true, document: newDocument };
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
      setAiProcessingProgress(0);
      return { success: false, error: err.message };
    }
  }, [generateDocId]);

  // Update document
  const updateDocument = useCallback((id, updates) => {
    setDocuments(prev =>
      prev.map(d =>
        d.id === id
          ? { ...d, ...updates, updatedAt: new Date().toISOString() }
          : d
      )
    );
    return { success: true };
  }, []);

  // Update document section
  const updateSection = useCallback((documentId, sectionId, updates) => {
    setDocuments(prev =>
      prev.map(d => {
        if (d.id !== documentId) return d;

        const updatedSections = d.sections.map(s =>
          s.id === sectionId
            ? { ...s, ...updates, lastModified: new Date().toISOString() }
            : s
        );

        // Recalculate progress and confidence
        const progress = calculateDocumentProgress(updatedSections);
        const confidenceScore = calculateAverageConfidence(updatedSections);

        return {
          ...d,
          sections: updatedSections,
          progress,
          confidenceScore,
          updatedAt: new Date().toISOString(),
        };
      })
    );
    return { success: true };
  }, []);

  // Mark section as complete
  const markSectionComplete = useCallback((documentId, sectionId) => {
    return updateSection(documentId, sectionId, { status: SECTION_STATUS.COMPLETED });
  }, [updateSection]);

  // Upload file to section
  const uploadSectionFile = useCallback(async (documentId, sectionId, file) => {
    setIsLoading(true);

    // Simulate file upload
    await new Promise(resolve => setTimeout(resolve, 1000));

    updateSection(documentId, sectionId, {
      uploadedFile: file.name,
      status: SECTION_STATUS.IN_PROGRESS,
    });

    setIsLoading(false);
    return { success: true, fileName: file.name };
  }, [updateSection]);

  // Generate content using AI
  const generateSectionContent = useCallback(async (documentId, sectionId) => {
    setIsLoading(true);
    setAiProcessingProgress(0);

    const progressInterval = setInterval(() => {
      setAiProcessingProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 15;
      });
    }, 200);

    // Simulate AI generation
    await new Promise(resolve => setTimeout(resolve, 2000));

    clearInterval(progressInterval);
    setAiProcessingProgress(100);

    // Mock generated content
    const generatedContent = `This is AI-generated content for the section. The content has been analyzed and synthesized from the uploaded source documents to create a comprehensive section that meets regulatory requirements. Additional details and data points have been incorporated to ensure completeness and accuracy.`;

    const confidenceScore = Math.floor(Math.random() * 20) + 75; // 75-95

    updateSection(documentId, sectionId, {
      content: generatedContent,
      confidenceScore,
      status: SECTION_STATUS.IN_PROGRESS,
    });

    setIsLoading(false);
    setAiProcessingProgress(0);
    
    return { success: true, content: generatedContent, confidenceScore };
  }, [updateSection]);

  // Change document status
  const changeStatus = useCallback((documentId, newStatus) => {
    updateDocument(documentId, { status: newStatus });
    
    // Add version history entry
    const doc = documents.find(d => d.id === documentId);
    if (doc) {
      const currentHistory = versionHistory[documentId] || [];
      const newVersion = {
        id: `VER-${documentId}-${currentHistory.length + 1}`,
        version: currentHistory.length + 1,
        documentId,
        createdBy: doc.createdBy,
        createdByName: doc.createdByName,
        action: 'STATUS_CHANGE',
        description: `Status changed to ${newStatus}`,
        createdAt: new Date().toISOString(),
        changes: [`Status: ${newStatus}`],
      };
      
      setVersionHistory(prev => ({
        ...prev,
        [documentId]: [...(prev[documentId] || []), newVersion],
      }));
    }
    
    return { success: true };
  }, [documents, versionHistory, updateDocument]);

  // Submit for review
  const submitForReview = useCallback((documentId, reviewerId, reviewerName) => {
    updateDocument(documentId, {
      status: DOCUMENT_STATUS.PENDING_REVIEW,
      assignedReviewer: reviewerId,
      assignedReviewerName: reviewerName,
    });
    return { success: true };
  }, [updateDocument]);

  // Approve document
  const approveDocument = useCallback((documentId) => {
    return changeStatus(documentId, DOCUMENT_STATUS.APPROVED);
  }, [changeStatus]);

  // Reject document
  const rejectDocument = useCallback((documentId, reason) => {
    changeStatus(documentId, DOCUMENT_STATUS.REJECTED);
    return { success: true };
  }, [changeStatus]);

  // Add comment
  const addComment = useCallback((documentId, commentData) => {
    const newComment = {
      id: `CMT-${Date.now()}`,
      documentId,
      ...commentData,
      status: 'OPEN',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      replies: [],
    };

    setComments(prev => ({
      ...prev,
      [documentId]: [...(prev[documentId] || []), newComment],
    }));

    return { success: true, comment: newComment };
  }, []);

  // Reply to comment
  const replyToComment = useCallback((documentId, commentId, replyData) => {
    const newReply = {
      id: `RPL-${Date.now()}`,
      ...replyData,
      createdAt: new Date().toISOString(),
    };

    setComments(prev => ({
      ...prev,
      [documentId]: (prev[documentId] || []).map(c =>
        c.id === commentId
          ? { ...c, replies: [...c.replies, newReply], updatedAt: new Date().toISOString() }
          : c
      ),
    }));

    return { success: true, reply: newReply };
  }, []);

  // Resolve comment
  const resolveComment = useCallback((documentId, commentId) => {
    setComments(prev => ({
      ...prev,
      [documentId]: (prev[documentId] || []).map(c =>
        c.id === commentId
          ? { ...c, status: 'RESOLVED', updatedAt: new Date().toISOString() }
          : c
      ),
    }));

    return { success: true };
  }, []);

  // Get comments for document
  const getComments = useCallback((documentId) => {
    return comments[documentId] || [];
  }, [comments]);

  // Get version history for document
  const getVersionHistory = useCallback((documentId) => {
    return versionHistory[documentId] || [];
  }, [versionHistory]);

  // Search documents
  const searchDocuments = useCallback((query) => {
    const lowerQuery = query.toLowerCase();
    return documents.filter(d =>
      d.title.toLowerCase().includes(lowerQuery) ||
      d.templateName.toLowerCase().includes(lowerQuery) ||
      d.createdByName.toLowerCase().includes(lowerQuery)
    );
  }, [documents]);

  // Delete document
  const deleteDocument = useCallback((id) => {
    setDocuments(prev => prev.filter(d => d.id !== id));
    setComments(prev => {
      const newComments = { ...prev };
      delete newComments[id];
      return newComments;
    });
    setVersionHistory(prev => {
      const newHistory = { ...prev };
      delete newHistory[id];
      return newHistory;
    });
    return { success: true };
  }, []);

  // Statistics
  const statistics = useMemo(() => ({
    total: documents.length,
    draft: documents.filter(d => d.status === DOCUMENT_STATUS.DRAFT).length,
    inProgress: documents.filter(d => d.status === DOCUMENT_STATUS.IN_PROGRESS).length,
    pendingReview: documents.filter(d => d.status === DOCUMENT_STATUS.PENDING_REVIEW).length,
    inReview: documents.filter(d => d.status === DOCUMENT_STATUS.IN_REVIEW).length,
    approved: documents.filter(d => d.status === DOCUMENT_STATUS.APPROVED).length,
    rejected: documents.filter(d => d.status === DOCUMENT_STATUS.REJECTED).length,
  }), [documents]);

  // Reset to initial mock data
  const resetDocuments = useCallback(() => {
    setDocuments(MOCK_DOCUMENTS);
    setComments(MOCK_COMMENTS);
    setVersionHistory(MOCK_VERSION_HISTORY);
    localStorage.removeItem(STORAGE_KEYS.DOCUMENTS);
    localStorage.removeItem(STORAGE_KEYS.COMMENTS);
    localStorage.removeItem(STORAGE_KEYS.VERSIONS);
  }, []);

  return {
    documents,
    isLoading,
    error,
    aiProcessingProgress,
    statistics,
    getAllDocuments,
    getDocument,
    getDocumentsByStatus,
    getDocumentsByUser,
    getDocumentsForReviewer,
    createDocument,
    updateDocument,
    updateSection,
    markSectionComplete,
    uploadSectionFile,
    generateSectionContent,
    changeStatus,
    submitForReview,
    approveDocument,
    rejectDocument,
    deleteDocument,
    addComment,
    replyToComment,
    resolveComment,
    getComments,
    getVersionHistory,
    searchDocuments,
    resetDocuments,
  };
}

export default useDocuments;
